import React from 'react';
import {
    VStack,
    Box,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Text,
    Heading,
    Divider,
    HStack,
    Icon,
    Badge,
    SimpleGrid,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon
} from '@chakra-ui/react';
import { FaGem, FaCrown, FaCoins, FaStar, FaKey, FaDharmachakra, FaTicketAlt, FaRandom, FaTrophy, FaChartLine, FaInfoCircle } from 'react-icons/fa';
import { useAuction } from '../../../context/AuctionContext';
import { useGame } from '../../context/GameContext';
import RTPInfo from './RTPInfo';

const SymbolRow = ({ icon, symbol, payout, type, description }) => (
    <HStack spacing={2} w="100%" p={1} borderRadius="md" bg="whiteAlpha.100" _hover={{ bg: "whiteAlpha.200" }}>
        <Icon as={icon} boxSize={4} color={
            type === 'Common' ? "gray.300" :
            type === 'Uncommon' ? "green.300" :
            type === 'Rare' ? "blue.300" :
            type === 'Epic' ? "purple.300" :
            type === 'Legendary' ? "orange.300" :
            type === 'Special' ? "yellow.300" : "white"
        } />
        <Box flex={1}>
            <HStack justify="space-between">
                <Text color="whiteAlpha.900" fontWeight="bold">{symbol}</Text>
                <Badge colorScheme={
                    type === 'Common' ? "gray" :
                    type === 'Uncommon' ? "green" :
                    type === 'Rare' ? "blue" :
                    type === 'Epic' ? "purple" :
                    type === 'Legendary' ? "orange" :
                    type === 'Special' ? "yellow" : "gray"
                }>
                    {type}
                </Badge>
            </HStack>
            <Text fontSize="sm" color="whiteAlpha.700">{description}</Text>
            {payout > 0 && <Text fontSize="sm" color="green.300">Payout: {payout}×</Text>}
        </Box>
    </HStack>
);

const GameInfo = () => {
    const { rewardsPool } = useAuction();
    const { gameState } = useGame();

    const symbolPayouts = [
        { symbol: '🍒🍒🍒', payout: '1x', probability: '1 in 5' },
        { symbol: '🍋🍋🍋', payout: '2x', probability: '1 in 12' },
        { symbol: '🍎🍎🍎', payout: '3x', probability: '1 in 25' },
        { symbol: '🫐🫐🫐', payout: '4x', probability: '1 in 35' },
        { symbol: '🍌🍌🍌', payout: '5x', probability: '1 in 50' },
        { symbol: '🍉🍉🍉', payout: '10x', probability: '1 in 100' },
        { symbol: '🍊🍊🍊', payout: '15x', probability: '1 in 200' },
        { symbol: '🍇🍇🍇', payout: '25x', probability: '1 in 400' },
        { symbol: '🍏🍏🍏', payout: '40x', probability: '1 in 800' },
        { symbol: '🍆🍆🍆', payout: '75x', probability: '1 in 1,500' },
        { symbol: '👑👑👑', payout: '125x', probability: '1 in 1,900' },
        { symbol: '🍄🍄🍄', payout: '250x', probability: '1 in 3,800' },
        { symbol: '⭐⭐⭐', payout: '500x', probability: '1 in 7,600' },
        { symbol: '🔔🔔🔔', payout: '1,000x', probability: '1 in 14,250' },
        { symbol: '🍀🍀🍀', payout: '5,000x', probability: '1 in 28,500' },
        { symbol: '🌶️🌶️🌶️', payout: '10,000x', probability: '1 in 47,500' }
    ];

    const matchMultipliers = [
        { matches: 3, multiplier: '1.0x (base)' },
        { matches: 4, multiplier: '1.1x' },
        { matches: 5, multiplier: '1.2x' },
        { matches: 6, multiplier: '1.3x' },
        { matches: 7, multiplier: '1.4x' },
        { matches: 8, multiplier: '1.5x' },
        { matches: 9, multiplier: '1.6x' },
        { matches: 10, multiplier: '1.7x' },
        { matches: 11, multiplier: '1.8x' },
        { matches: 12, multiplier: '1.9x' }
    ];

    const specialSymbols = [
        { symbol: '🎟️', effect: 'Lottery Ticket (1 in 2 games)' },
        { symbol: '🎁', effect: 'Bonus Game with 5x-100x multiplier (1 in 950 games)' }
    ];

    return (
        <VStack spacing={4} align="stretch">
            {/* RTP Information */}
            <RTPInfo />
            
            <Accordion allowToggle defaultIndex={[0]} borderColor="whiteAlpha.200">
                <AccordionItem border="none">
                    <AccordionButton px={1} _hover={{ bg: "transparent" }}>
                        <Box flex="1" textAlign="left">
                            <Heading size="sm" color="purple.400" display="flex" alignItems="center">
                                <Icon as={FaGem} mr={2} />
                                Symbols & Payouts
                            </Heading>
                        </Box>
                        <AccordionIcon color="purple.400" />
                    </AccordionButton>
                    <AccordionPanel pb={4} px={1}>
                        <SimpleGrid columns={1} spacing={2}>
                            <Box>
                                <Heading size="sm" mb={3}>Symbol Payouts</Heading>
                                <Table variant="simple" size="sm" fontSize="0.9em">
                                    <Thead>
                                        <Tr>
                                            <Th>Symbol</Th>
                                            <Th>Base Payout</Th>
                                            <Th>Probability</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {symbolPayouts.map((item, index) => (
                                            <Tr key={index}>
                                                <Td style={{ fontSize: '1.32em', lineHeight: '1.32em' }}>{item.symbol}</Td>
                                                <Td>{item.payout}</Td>
                                                <Td>{item.probability}</Td>
                                            </Tr>
                                        ))}
                                    </Tbody>
                                </Table>
                            </Box>
                        </SimpleGrid>
                        <Text fontSize="xs" color="whiteAlpha.600" mt={2} textAlign="center">
                            Scroll for more symbols
                        </Text>
                    </AccordionPanel>
                </AccordionItem>

                <AccordionItem border="none" mt={2}>
                    <AccordionButton px={1} _hover={{ bg: "transparent" }}>
                        <Box flex="1" textAlign="left">
                            <Heading size="sm" color="purple.400" display="flex" alignItems="center">
                                <Icon as={FaInfoCircle} mr={2} />
                                Special Symbols
                            </Heading>
                        </Box>
                        <AccordionIcon color="purple.400" />
                    </AccordionButton>
                    <AccordionPanel pb={4} px={1}>
                        <VStack spacing={2} align="stretch">
                            <Box>
                                <Heading size="sm" mb={2}>Special Symbols</Heading>
                                <Table variant="simple" size="sm" fontSize="0.9em">
                                    <Thead>
                                        <Tr>
                                            <Th>Symbol</Th>
                                            <Th>Effect</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {specialSymbols.map((item, index) => (
                                            <Tr key={index}>
                                                <Td style={{ fontSize: '1.32em', lineHeight: '1.32em' }}>{item.symbol}</Td>
                                                <Td>{item.effect}</Td>
                                            </Tr>
                                        ))}
                                    </Tbody>
                                </Table>
                            </Box>
                        </VStack>
                    </AccordionPanel>
                </AccordionItem>

                <AccordionItem border="none" mt={2}>
                    <AccordionButton px={1} _hover={{ bg: "transparent" }}>
                        <Box flex="1" textAlign="left">
                            <Heading size="sm" color="purple.400" display="flex" alignItems="center">
                                <Icon as={FaStar} mr={2} />
                                Match Multipliers
                            </Heading>
                        </Box>
                        <AccordionIcon color="purple.400" />
                    </AccordionButton>
                    <AccordionPanel pb={4} px={1}>
                        <VStack spacing={2} align="stretch" bg="whiteAlpha.100" p={3} borderRadius="md">
                            <Box>
                                <Heading size="sm" mb={2}>Match Multipliers</Heading>
                                <Table variant="simple" size="sm" fontSize="0.9em">
                                    <Thead>
                                        <Tr>
                                            <Th>Matches</Th>
                                            <Th>Multiplier</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {matchMultipliers.map((item, index) => (
                                            <Tr key={index}>
                                                <Td>{item.matches}</Td>
                                                <Td>{item.multiplier}</Td>
                                            </Tr>
                                        ))}
                                    </Tbody>
                                </Table>
                            </Box>
                        </VStack>
                    </AccordionPanel>
                </AccordionItem>
            </Accordion>

            <Divider borderColor="whiteAlpha.200" />

            <Box>
                <Heading size="sm" mb={2} color="purple.400" display="flex" alignItems="center">
                    <Icon as={FaTicketAlt} mr={2} />
                    Your Stats
                </Heading>
                <VStack spacing={3} align="stretch" bg="whiteAlpha.100" p={4} borderRadius="md">
                    <HStack justify="space-between">
                        <Text color="whiteAlpha.900">Cards Played:</Text>
                        <Text color="purple.400" fontWeight="bold">
                            {gameState.sessionStats.totalGames || 0}
                        </Text>
                    </HStack>
                    <HStack justify="space-between">
                        <Text color="whiteAlpha.900">Bonus Games:</Text>
                        <Text color="yellow.400" fontWeight="bold">
                            {gameState.sessionStats.bonusGames || 0}
                        </Text>
                    </HStack>
                    <HStack justify="space-between">
                        <Text color="whiteAlpha.900">Total Wins:</Text>
                        <Text color="green.400" fontWeight="bold">
                            {gameState.sessionStats.totalWins || 0}
                        </Text>
                    </HStack>
                </VStack>
            </Box>
        </VStack>
    );
};

export default GameInfo;
